/**
 * liveBoardPublish.js — the spectator feed's client half.
 *
 * 🔴 THE ONE RULE: THIS MUST NEVER BLOCK, DELAY, OR THROW INTO THE ENTRY PATH.
 *
 * If Upstash is down at 8pm on August 22, Ben keeps drafting off localStorage
 * and only the spectators lose the feed. That is the acceptance criterion for
 * the whole share-link feature, and it is enforced here rather than in the API
 * route, because a route cannot protect a caller that awaits it.
 *
 * How that is guaranteed, concretely:
 *   - `publishBoard` is NOT async and returns nothing awaitable. There is
 *     nothing for a commit handler to accidentally await.
 *   - The whole body is inside try/catch, and the fetch carries `.catch()`, so
 *     neither a synchronous throw (no fetch in the environment) nor a rejected
 *     promise can escape. An unhandled rejection here would surface as an
 *     error overlay on a screen eleven people are watching.
 *   - No retries. A failed publish is simply skipped — the NEXT pick publishes
 *     the whole board again, so one dropped POST self-heals within one pick and
 *     a retry storm can never form.
 *   - The caller writes localStorage FIRST, unconditionally. This runs after,
 *     as a side effect, and its return value is ignored.
 *
 * `keepalive` matches the pattern in useMockDraft.js:921 — it lets the last
 * publish survive the tab closing.
 */

const ENDPOINT = '/api/live-board';

/**
 * The board, minus what the spectator feed has no use for.
 *
 * 🔴 posRank IS OUR BOARD, NOT THE PUBLIC ONE. Every sale carries the
 * POST-board-overrides rank — Mock 1's own editorial rank layer, rewritten at
 * module load from board-overrides.json. It is never rendered on /watch (see
 * the headers on PlayersLeft.jsx and WatchPage.jsx, which forbid exactly that),
 * but it was still being POSTed, so anyone holding the link could read our
 * ranking as JSON at /api/live-board?id=… — the eleven people the publisher is
 * bidding against, included. Publishing a field nobody renders is all cost.
 *
 * ⚠️ IT STAYS ON THE LOCAL RECORD. The sale keeps posRank in localStorage,
 * deliberately: the board moves, and a record written in August should still
 * price against the board it was written on (see liveBoard.js's data-model
 * header). This strips it from the WIRE, not from the board.
 *
 * ⚠️ ABSENT, NOT NULL. The API's per-sale validator treats posRank as optional
 * (`s.posRank != null && ...`) and rejects unexpected keys, so omitting it is
 * valid and needs no api/ change. A published board simply no longer has one.
 */
export function forPublication(board) {
  const sales = (board.sales || []).map(({ posRank, ...rest }) => rest);
  return { ...board, sales };
}

/**
 * Fire-and-forget. Returns nothing; callers must not await it.
 *
 * @param shareId  the board's capability id
 * @param board    the sanitized Live Board state
 * @param onStatus optional (ok: boolean) => void, for the quiet "not syncing"
 *                 indicator. Called asynchronously, never in the commit frame.
 */
export function publishBoard(shareId, board, onStatus) {
  if (!shareId || !board) return;
  try {
    const payload = JSON.stringify({ shareId, board: forPublication(board) });
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    })
      .then(r => { if (onStatus) onStatus(r.ok); })
      .catch(() => { if (onStatus) onStatus(false); });
  } catch {
    // fetch missing, body unserializable, anything at all — the entry path
    // does not find out.
    if (onStatus) { try { onStatus(false); } catch { /* not even this */ } }
  }
}

/**
 * Read a published board. Used only by the spectator view, which is allowed to
 * be async because nobody is typing into it.
 *
 * Resolves { status: 'ok' | 'waiting' | 'error', board, updatedAt }.
 * "waiting" is a first-class state, not a failure: a spectator who opens the
 * link before pick 1 has not hit an error.
 */
export async function fetchPublishedBoard(shareId, signal) {
  try {
    const res = await fetch(`${ENDPOINT}?id=${encodeURIComponent(shareId)}`, {
      signal, cache: 'no-store',
    });
    if (res.status === 404) return { status: 'waiting' };
    if (!res.ok) return { status: 'error' };
    const json = await res.json();
    if (!json?.ok || !json.board) return { status: 'waiting' };
    return { status: 'ok', board: json.board, updatedAt: json.updatedAt };
  } catch {
    return { status: 'error' };
  }
}

/**
 * An unguessable capability. Not sequential, not derived from the league name —
 * a share id that can be guessed from "Strong Island Fantasy" is not a
 * capability at all.
 *
 * crypto.randomUUID where available (every browser this app supports and the
 * node used by tests); the fallback is only for exotic environments and still
 * carries 128+ bits from getRandomValues where that exists.
 */
export function newShareId() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      const b = new Uint8Array(16);
      crypto.getRandomValues(b);
      return Array.from(b, x => x.toString(16).padStart(2, '0')).join('');
    }
  } catch { /* fall through */ }
  return `lb-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** The URL a spectator opens. Single path segment, matching /scout. */
export function shareUrl(shareId) {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}/watch?b=${encodeURIComponent(shareId)}`;
}
